import {Accordion, Badge} from '@mantine/core';
import {Group} from 'src/design/atoms/Group';
import {Text} from 'src/design/atoms/Text';
import {IconAlertTriangle, IconSettings} from '@tabler/icons-react';
import React, {useCallback, useEffect, useMemo} from 'react';
import {useFormState, useWatch} from 'react-hook-form';
import {useLocalStorage} from 'usehooks-ts';
import {useProactFormContext} from 'src/app/forms/engine/ProactFormContext';
import type {IFormDataV2} from 'src/types/FormV2';
import {CourseSettingsFieldGroups} from 'src/app/training/courseFormMeta';
import {
  courseSettingsGroupAnchorId,
  CourseSettingsPanel,
} from './CourseSettingsPanel';
import {buildCourseSettingsSummary} from './courseSettingsSummary';

const SECTION_VALUE = 'course-settings';

const settingsFieldNames = CourseSettingsFieldGroups.flatMap(
  group => group.fields
);

export interface CourseSettingsSectionProps {
  disabled?: boolean;
  /** Rendered as an extra card alongside the field groups. */
  extraCard?: React.ReactNode;
}

/**
 * Course settings as a collapsible header section above the Build canvas,
 * mirroring the Form Builder's settings accordion — with two deliberate
 * differences.
 *
 * The collapsed control renders a summary chip row rather than just a title,
 * so the configuration stays readable without expanding; clicking a chip
 * expands and scrolls to the group it describes. And a settings-field
 * validation error puts an issue badge on the control and force-expands the
 * section, so a failed Save can never leave the offending field off-screen.
 */
export const CourseSettingsSection: React.FC<CourseSettingsSectionProps> = ({
  disabled,
  extraCard,
}) => {
  const {methods, getDisplayValues} = useProactFormContext();
  const [expanded, setExpanded] = useLocalStorage<string | null>(
    'course_settings_expanded',
    SECTION_VALUE
  );

  const watchedSettings = useWatch({
    control: methods.control,
    name: settingsFieldNames,
  });
  const {errors} = useFormState({control: methods.control});

  const issueCount = settingsFieldNames.filter(name => errors[name]).length;

  useEffect(() => {
    if (issueCount > 0) setExpanded(SECTION_VALUE);
  }, [issueCount, setExpanded]);

  // Built from the `useWatch` values rather than `methods.getValues()`, which
  // is not a reactive dependency — memoising on it left the chips frozen at
  // their mount-time values.
  const chips = useMemo(() => {
    const values: IFormDataV2 = {};
    settingsFieldNames.forEach((name, index) => {
      values[name] = watchedSettings[index];
    });
    return buildCourseSettingsSummary(
      values,
      getDisplayValues().projectId as string | undefined
    );
  }, [watchedSettings, getDisplayValues]);

  const handleChipClick = useCallback(
    (group: string) => {
      setExpanded(SECTION_VALUE);
      window.requestAnimationFrame(() => {
        document
          .getElementById(courseSettingsGroupAnchorId(group))
          ?.scrollIntoView({behavior: 'smooth', block: 'nearest'});
      });
    },
    [setExpanded]
  );

  return (
    <Accordion
      value={expanded}
      variant="contained"
      multiple={false}
      onChange={setExpanded}>
      <Accordion.Item value={SECTION_VALUE}>
        <Accordion.Control icon={<IconSettings size={16} />}>
          <Group gap="xs" wrap="wrap">
            <Text size="sm" fw={600}>
              Course Settings
            </Text>
            {issueCount > 0 && (
              <Badge
                color="red"
                variant="light"
                size="sm"
                leftSection={<IconAlertTriangle size={12} />}>
                {issueCount} issue{issueCount === 1 ? '' : 's'}
              </Badge>
            )}
            {chips.map(chip => (
              <Badge
                key={`${chip.group}-${chip.label}`}
                component="span"
                role="button"
                tabIndex={0}
                variant={chip.muted ? 'outline' : 'light'}
                color={chip.muted ? 'gray' : 'blue'}
                size="sm"
                style={{cursor: 'pointer', textTransform: 'none'}}
                onClick={(event: React.MouseEvent) => {
                  event.stopPropagation();
                  handleChipClick(chip.group);
                }}>
                {chip.label}
              </Badge>
            ))}
          </Group>
        </Accordion.Control>
        <Accordion.Panel>
          <CourseSettingsPanel disabled={disabled} extraCard={extraCard} />
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
};
